import jsPDF from 'jspdf';

// Viubux Logo - use local file to avoid CORS issues
const VIUBUX_LOGO = '/viubux-logo.png';

// Font sizes
const FONT_SIZES = {
  title: 14,
  sectionHeader: 11,
  body: 9,
  small: 8,
  footer: 7
};

// Colors
const COLORS = {
  primary: [170, 42, 255],      // #aa2aff
  secondary: [39, 43, 150],     // #272b96
  darkBg: [15, 19, 58],         // #0f133a
  text: [50, 50, 50],
  muted: [120, 120, 120],
  white: [255, 255, 255],
  lightBg: [248, 248, 252],
  green: [34, 197, 94]
};

/**
 * Format currency value respecting the selected currency
 */
function formatCurrency(value, curr = 'MXN', exRate = 17.5) {
  if (typeof value !== 'number' || isNaN(value)) return '$0';
  if (curr === 'USD') {
    const usdValue = value / exRate;
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(usdValue);
  }
  return new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);
}

/**
 * Load image as base64
 */
async function loadImageAsBase64(url) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading image:', error);
    return null;
  }
}

/**
 * Add footer to PDF page
 */
function addFooter(pdf, pdfWidth, pdfHeight, margin, pageNum, totalPages) {
  const footerY = pdfHeight - 15;
  
  // Footer line
  pdf.setDrawColor(...COLORS.primary);
  pdf.setLineWidth(0.3);
  pdf.line(margin, footerY, pdfWidth - margin, footerY);
  
  // Contact info
  pdf.setFontSize(FONT_SIZES.footer);
  pdf.setTextColor(...COLORS.secondary);
  pdf.text('www.Viubux.com  •  Tel: 55 4856 4696  •  email@viubux.com', pdfWidth / 2, footerY + 4, { align: 'center' });
  
  // Page number
  pdf.setTextColor(...COLORS.muted);
  pdf.text(`Página ${pageNum} de ${totalPages}`, pdfWidth - margin, footerY + 4, { align: 'right' });
}

/**
 * Draw a table row with VC discount (strikethrough original + green discounted)
 */
function drawDiscountTableRow(pdf, x, y, label, originalValue, discountedValue, width) {
  const rowHeight = 8;
  pdf.setTextColor(...COLORS.text);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(FONT_SIZES.body);
  
  const maxLabelWidth = width * 0.55;
  const lines = pdf.splitTextToSize(label, maxLabelWidth);
  const actualHeight = Math.max(rowHeight, lines.length * 4);
  
  lines.forEach((line, i) => {
    pdf.text(line, x + 2, y + (i * 4));
  });
  
  // Original price - red strikethrough
  pdf.setTextColor(220, 80, 80);
  pdf.setFontSize(6);
  const origWidth = pdf.getTextWidth(originalValue);
  const origX = x + width - 2 - origWidth;
  pdf.text(originalValue, origX, y - 1);
  // Strikethrough line
  pdf.setDrawColor(220, 80, 80);
  pdf.setLineWidth(0.3);
  pdf.line(origX - 1, y - 2, origX + origWidth + 1, y - 2);
  
  // Discounted price - green bold
  pdf.setTextColor(34, 139, 34);
  pdf.setFontSize(FONT_SIZES.body);
  pdf.setFont('helvetica', 'bold');
  pdf.text(discountedValue, x + width - 2, y + 3, { align: 'right' });
  pdf.setFont('helvetica', 'normal');
  
  return y + actualHeight;
}

/**
 * Draw a table row
 */
function drawTableRow(pdf, x, y, label, value, width, options = {}) {
  const { isHeader = false, isBold = false, bgColor = null } = options;
  const rowHeight = 6;
  
  if (bgColor) {
    pdf.setFillColor(...bgColor);
    pdf.rect(x, y - 4, width, rowHeight, 'F');
  }
  
  if (isHeader) {
    pdf.setFillColor(...COLORS.lightBg);
    pdf.rect(x, y - 4, width, rowHeight, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...COLORS.secondary);
  } else {
    pdf.setTextColor(...COLORS.text);
    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
  }
  
  pdf.setFontSize(FONT_SIZES.body);
  
  // Split long labels
  const maxLabelWidth = width * 0.65;
  const lines = pdf.splitTextToSize(label, maxLabelWidth);
  pdf.text(lines[0], x + 2, y);
  pdf.text(value, x + width - 2, y, { align: 'right' });
  
  return y + rowHeight;
}

/**
 * Export quote data to a professionally styled PDF matching QuotePreview design
 */
export async function exportQuoteToPDF(previewElement, quoteData = {}) {
  const { 
    bookTitle, 
    customerName,
    customerEmail,
    customerPhone,
    coverUrl,
    audiobookData,
    voiceCloningCost = 0, talentoPremiumCost = 0, extraVoicesUltraCost = 0, extraVoicesUltra = 0,
    multivozCost = 0, multivozActive = false, multivozVoces = 0,
    revisionAudioCost = 0, revisionAudio = 'rev_none',
    masterizacionCost = 0, masterizacion = 'master_none',
    podcastData,
    meditacionesData,
    audiocursosData,
    addonsData,
    totals,
    quoteNumber,
    currency: quoteCurrency,
    exchangeRate: quoteExchangeRate
  } = quoteData;
  
  const pdfCurrency = quoteCurrency || 'MXN';
  const pdfExRate = quoteExchangeRate || 17.5;
  const showVCDiscount = totals?.viubuxConnectActive && totals?.showDiscountPerItem;
  
  // Helper to format prices with the correct currency
  const fp = (v) => formatCurrency(v, pdfCurrency, pdfExRate);
  
  // Helper: draw row with or without VC discount
  const drawRow = (pdf, x, y, label, price, width, options = {}) => {
    if (showVCDiscount && price > 0 && !options.noDiscount) {
      return drawDiscountTableRow(pdf, x, y, label, fp(price), fp(price * 0.85), width);
    }
    return drawTableRow(pdf, x, y, label, options.prefix ? `${options.prefix}${fp(price)}` : fp(price), width, options);
  };
  
  // Generate quote number if not provided
  const finalQuoteNumber = quoteNumber || `VBX-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  
  // Generate filename
  const date = new Date().toISOString().split('T')[0];
  let filename = `cotizacion-viubux-${date}`;
  
  if (bookTitle) {
    const sanitizedTitle = bookTitle.replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ\s]/g, '').toLowerCase().replace(/\s+/g, '-').slice(0, 30);
    filename = `cotizacion-${sanitizedTitle}-${date}`;
  } else if (customerName) {
    const sanitizedName = customerName.replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ\s]/g, '').toLowerCase().replace(/\s+/g, '-').slice(0, 30);
    filename = `cotizacion-${sanitizedName}-${date}`;
  }

  // Create PDF
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pdfWidth - (margin * 2);
  let yPos = margin;

  // Helper: draw a horizontal gradient rectangle from #272b96 to #aa2aff
  const drawGradientRect = (x, y, w, h, steps = 20) => {
    const sw = w / steps;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      pdf.setFillColor(
        Math.round(39 + (170 - 39) * t),
        Math.round(43 + (42 - 43) * t),
        Math.round(150 + (255 - 150) * t)
      );
      pdf.rect(x + i * sw, y, sw + 0.5, h, 'F');
    }
  };

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredSpace) => {
    if (yPos + requiredSpace > pdfHeight - 25) {
      pdf.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // ========== HEADER WITH GRADIENT BACKGROUND ==========
  // Draw gradient from #272b96 to #aa2aff
  const headerHeight = 28;
  drawGradientRect(0, 0, pdfWidth, headerHeight, 40);
  
  // Try to load and add logo from local path (correct aspect ratio 3.04:1)
  try {
    const logoUrl = window.location.origin + VIUBUX_LOGO;
    const logoBase64 = await loadImageAsBase64(logoUrl);
    if (logoBase64) {
      pdf.addImage(logoBase64, 'PNG', margin, 5, 36, 12);
    } else {
      // Fallback: text logo
      pdf.setFontSize(16);
      pdf.setTextColor(...COLORS.white);
      pdf.setFont('helvetica', 'bold');
      pdf.text('VIUBUX', margin, 16);
    }
  } catch (e) {
    // Fallback: text logo
    pdf.setFontSize(16);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('VIUBUX', margin, 16);
  }
  
  // "Cotización" text on right
  pdf.setFontSize(10);
  pdf.setTextColor(...COLORS.white);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Cotización', pdfWidth - margin, 8, { align: 'right' });
  
  // Quote Number
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text(finalQuoteNumber, pdfWidth - margin, 15, { align: 'right' });
  
  // Date
  const today = new Date().toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(today, pdfWidth - margin, 21, { align: 'right' });

  yPos = 35;

  // ========== PROJECT NAME ==========
  const pdfProjectName = quoteData.projectName;
  const pdfProjectCoverUrl = quoteData.projectCoverUrl;
  
  if (pdfProjectCoverUrl && !audiobookData) {
    try {
      const coverBase64 = await loadImageAsBase64(pdfProjectCoverUrl);
      if (coverBase64) {
        const coverH = 35;
        const coverW = coverH * 0.75;
        const coverX = (pdfWidth - coverW) / 2;
        pdf.addImage(coverBase64, 'JPEG', coverX, yPos, coverW, coverH);
        yPos += coverH + 5;
      }
    } catch (e) {
      console.warn('Project cover load failed:', e);
    }
  }
  
  if (pdfProjectName) {
    pdf.setFontSize(14);
    pdf.setTextColor(...COLORS.primary);
    pdf.setFont('helvetica', 'bold');
    pdf.text(pdfProjectName, pdfWidth / 2, yPos, { align: 'center' });
    yPos += 10;
  }

  // ========== CUSTOMER INFO ==========
  if (customerName) {
    pdf.setFontSize(FONT_SIZES.body);
    pdf.setTextColor(...COLORS.muted);
    pdf.text('Cliente:', margin, yPos);
    pdf.setTextColor(...COLORS.text);
    pdf.setFont('helvetica', 'bold');
    pdf.text(customerName, margin + 18, yPos);
    yPos += 8;
  }

  // ========== AUDIOBOOK SECTION ==========
  if (audiobookData && audiobookData.packageName) {
    checkPageBreak(70);
    
    // Book info with cover
    const hasBookCover = coverUrl && coverUrl.length > 0;
    let coverX = margin;
    let infoX = margin;
    
    if (hasBookCover) {
      try {
        const coverBase64 = await loadImageAsBase64(coverUrl);
        if (coverBase64) {
          pdf.addImage(coverBase64, 'JPEG', margin, yPos, 25, 35);
          infoX = margin + 30;
        }
      } catch (e) {
        console.log('Could not load cover image');
      }
    }
    
    // Book title and author
    if (audiobookData.bookTitle) {
      pdf.setFontSize(12);
      pdf.setTextColor(...COLORS.text);
      pdf.setFont('helvetica', 'bold');
      pdf.text(audiobookData.bookTitle, infoX, yPos + 5);
    }
    if (audiobookData.authorName) {
      pdf.setFontSize(FONT_SIZES.body);
      pdf.setTextColor(...COLORS.muted);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`por ${audiobookData.authorName}`, infoX, yPos + 11);
    }
    
    // Page count and duration
    const infoLine = [];
    if (audiobookData.pageCount) infoLine.push(`${audiobookData.pageCount} págs`);
    if (audiobookData.durationHours) infoLine.push(`${audiobookData.durationHours.toFixed(1)}h de audio`);
    if (infoLine.length > 0) {
      pdf.setFontSize(FONT_SIZES.small);
      pdf.setTextColor(...COLORS.muted);
      pdf.text(infoLine.join(' • '), infoX, yPos + 17);
    }
    
    yPos += hasBookCover ? 40 : 25;

    // Package box with gradient
    drawGradientRect(margin, yPos, contentWidth, 12);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Paquete ${audiobookData.packageName}`, margin + 5, yPos + 8);
    yPos += 18;

    // Price breakdown table
    const tableX = margin;
    const tableWidth = contentWidth;
    
    if (audiobookData.finalPricePerHour) {
      yPos = drawTableRow(pdf, tableX, yPos, `Precio/hora (${audiobookData.packageName})`, fp(audiobookData.finalPricePerHour), tableWidth);
    }
    if (audiobookData.durationHours && audiobookData.finalPricePerHour) {
      yPos = drawTableRow(pdf, tableX, yPos, 
        `Duración × Precio (${audiobookData.durationHours.toFixed(1)}h × ${fp(audiobookData.finalPricePerHour)})`, 
        fp(audiobookData.subtotal), tableWidth);
    }
    
    // Audiobook addons (if any and if audiobook is selected)
    if (addonsData && addonsData.length > 0 && audiobookData) {
      addonsData.forEach(addon => {
        yPos = drawRow(pdf, tableX, yPos, addon.name, addon.price, tableWidth, { prefix: '+' });
      });
    }

    // Voice Cloning & extras
    if (voiceCloningCost > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Clonación de Voz Original (pago único)', voiceCloningCost, tableWidth, { prefix: '+' });
    }
    if (talentoPremiumCost > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Upgrade Talento Premium', talentoPremiumCost, tableWidth, { prefix: '+' });
    }
    if (extraVoicesUltraCost > 0) {
      yPos = drawRow(pdf, tableX, yPos, `Voces Clonadas Adicionales (${extraVoicesUltra})`, extraVoicesUltraCost, tableWidth, { prefix: '+' });
    }
    if (multivozCost > 0) {
      yPos = drawRow(pdf, tableX, yPos, `Multivoz (${multivozVoces} voz/es)`, multivozCost, tableWidth, { prefix: '+' });
    }
    if (revisionAudioCost > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Revisión de Audio para Distribución', revisionAudioCost, tableWidth, { prefix: '+' });
    }
    if (masterizacionCost > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Masterización de Audiolibro', masterizacionCost, tableWidth, { prefix: '+' });
    }
    
    // Subtotal
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tableX, yPos - 2, tableX + tableWidth, yPos - 2);
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Audiolibro', fp(totals?.audiobookSubtotal || (audiobookData.subtotal + voiceCloningCost + extraVoicesUltraCost + multivozCost + revisionAudioCost + masterizacionCost)), tableWidth, { isBold: true });
    
    yPos += 5;
  }

  // ========== SERVICIOS DE AUDIO (standalone - when no audiobook) ==========
  if (!audiobookData && (revisionAudioCost > 0 || masterizacionCost > 0)) {
    checkPageBreak(30);
    
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Servicios de Audio', margin + 5, yPos + 5.5);
    yPos += 14;

    const tableX = margin;
    const tableWidth = contentWidth;
    
    if (revisionAudioCost > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Revisión de Archivos de Audio', revisionAudioCost, tableWidth, { prefix: '+' });
    }
    if (masterizacionCost > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Masterización de Audiolibro', masterizacionCost, tableWidth, { prefix: '+' });
    }
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Servicios de Audio', fp(revisionAudioCost + masterizacionCost), tableWidth, { isBold: true });
    yPos += 5;
  }

  // ========== PODCAST SECTION ==========
  if (podcastData && podcastData.packageName) {
    checkPageBreak(40);
    
    // Section header
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Podcast', margin + 5, yPos + 5.5);
    if (podcastData.quantity > 1) {
      pdf.setFontSize(FONT_SIZES.small);
      pdf.text(`(${podcastData.quantity} programas)`, margin + 25, yPos + 5.5);
    }
    yPos += 14;

    const tableX = margin;
    const tableWidth = contentWidth;
    
    if (podcastData.recordingHours > 0) {
      yPos = drawTableRow(pdf, tableX, yPos, 'Horas de Grabación Estimadas', `${podcastData.recordingHours.toFixed(1)} hrs`, tableWidth);
    }
    
    const podcastMultiplier = podcastData.postProduction ? 2 : 1;
    const podcastLineLabel = `Paquete ${podcastData.packageName}${podcastData.postProduction ? ' + Post-Producción' : ''}`;
    const podcastUnitPrice = (podcastData.pricePerUnit || 0) * podcastMultiplier;
    yPos = drawTableRow(pdf, tableX, yPos, podcastLineLabel, fp(podcastUnitPrice), tableWidth);
    if (podcastData.packageDescription) {
      const desc = podcastData.postProduction
        ? podcastData.packageDescription.replace(/no incluye post-producción\.?/i, 'Incluye Post-Producción. Materiales terminados vía nube tras finiquito.')
        : podcastData.packageDescription;
      pdf.setFontSize(FONT_SIZES.small);
      pdf.setTextColor(...COLORS.muted);
      const wrapped = pdf.splitTextToSize(desc, tableWidth - 4);
      pdf.text(wrapped, tableX + 2, yPos);
      yPos += wrapped.length * 3 + 1;
      pdf.setFontSize(FONT_SIZES.body);
      pdf.setTextColor(...COLORS.text);
    }
    
    if (podcastData.addons && podcastData.addons.length > 0) {
      podcastData.addons.forEach(addon => {
        yPos = drawTableRow(pdf, tableX, yPos, addon, '', tableWidth);
      });
    }
    
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tableX, yPos - 2, tableX + tableWidth, yPos - 2);
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Podcast', fp(podcastData.subtotal), tableWidth, { isBold: true });
    
    yPos += 5;
  }

  // ========== MEDITACIONES SECTION ==========
  if (meditacionesData && meditacionesData.packageName) {
    checkPageBreak(40);
    
    // Section header
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Meditaciones Guiadas', margin + 5, yPos + 5.5);
    if (meditacionesData.quantity > 1) {
      pdf.setFontSize(FONT_SIZES.small);
      pdf.text(`(${meditacionesData.quantity} sesiones)`, margin + 50, yPos + 5.5);
    }
    yPos += 14;

    const tableX = margin;
    const tableWidth = contentWidth;
    
    if (meditacionesData.recordingHours > 0) {
      yPos = drawTableRow(pdf, tableX, yPos, 'Horas de Producción Estimadas', `${meditacionesData.recordingHours.toFixed(1)} hrs`, tableWidth);
    }
    
    const priceText = meditacionesData.recordingHours > 0 
      ? `${fp(meditacionesData.pricePerUnit)}/hr × ${meditacionesData.recordingHours.toFixed(1)}`
      : fp(meditacionesData.pricePerUnit);
    yPos = drawTableRow(pdf, tableX, yPos, `Paquete ${meditacionesData.packageName}`, priceText, tableWidth);
    
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tableX, yPos - 2, tableX + tableWidth, yPos - 2);
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Meditaciones', fp(meditacionesData.subtotal), tableWidth, { isBold: true });
    
    yPos += 5;
  }

  // ========== AUDIOCURSOS SECTION ==========
  if (audiocursosData && audiocursosData.packageName) {
    checkPageBreak(40);
    
    // Section header
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Cursos (A/V)', margin + 5, yPos + 5.5);
    if (audiocursosData.quantity > 1) {
      pdf.setFontSize(FONT_SIZES.small);
      pdf.text(`(${audiocursosData.quantity} lecciones)`, margin + 35, yPos + 5.5);
    }
    yPos += 14;

    const tableX = margin;
    const tableWidth = contentWidth;
    
    if (audiocursosData.recordingHours > 0) {
      yPos = drawTableRow(pdf, tableX, yPos, 'Horas de Grabación Estimadas', `${audiocursosData.recordingHours.toFixed(1)} hrs`, tableWidth);
    }
    
    const priceText = audiocursosData.recordingHours > 0 
      ? `${fp(audiocursosData.pricePerUnit)}/hr × ${audiocursosData.recordingHours.toFixed(1)}`
      : fp(audiocursosData.pricePerUnit);
    yPos = drawTableRow(pdf, tableX, yPos, `Paquete ${audiocursosData.packageName}`, priceText, tableWidth);
    
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tableX, yPos - 2, tableX + tableWidth, yPos - 2);
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Cursos (A/V)', fp(audiocursosData.subtotal), tableWidth, { isBold: true });
    
    yPos += 5;
  }

  // ========== WALKING PODCAST SECTION ==========
  const walkingPodcastData = quoteData.walkingPodcastData;
  if (walkingPodcastData && walkingPodcastData.packageName) {
    checkPageBreak(40);
    
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Walking Podcast', margin + 5, yPos + 5.5);
    if (walkingPodcastData.quantity > 1) {
      pdf.setFontSize(FONT_SIZES.small);
      pdf.text(`(${walkingPodcastData.quantity} programas)`, margin + 45, yPos + 5.5);
    }
    yPos += 14;

    const tableX = margin;
    const tableWidth = contentWidth;
    
    if (walkingPodcastData.recordingHours > 0) {
      yPos = drawTableRow(pdf, tableX, yPos, 'Horas de Grabación Estimadas', `${walkingPodcastData.recordingHours.toFixed(1)} hrs`, tableWidth);
    }
    
    const priceText = walkingPodcastData.recordingHours > 0
      ? `${fp(walkingPodcastData.pricePerUnit)}/hr × ${walkingPodcastData.recordingHours.toFixed(1)}`
      : fp(walkingPodcastData.pricePerUnit);
    yPos = drawTableRow(pdf, tableX, yPos, `Paquete ${walkingPodcastData.packageName}`, priceText, tableWidth);
    
    if (walkingPodcastData.sonidoDirectoTotal > 0) {
      yPos = drawTableRow(pdf, tableX, yPos, 'Sonido Directo', `+${fp(walkingPodcastData.sonidoDirectoTotal)}`, tableWidth);
    }
    if (walkingPodcastData.asistenteTotal > 0) {
      yPos = drawTableRow(pdf, tableX, yPos, 'Asistente', `+${fp(walkingPodcastData.asistenteTotal)}`, tableWidth);
    }
    
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tableX, yPos - 2, tableX + tableWidth, yPos - 2);
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Walking Podcast', fp(walkingPodcastData.subtotal), tableWidth, { isBold: true });
    
    yPos += 5;
  }

  // ========== INDEPENDENT ADDONS SECTION (when no audiobook) ==========
  if (addonsData && addonsData.length > 0 && !audiobookData) {
    checkPageBreak(30 + addonsData.length * 6);
    
    // Section header
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Servicios Adicionales', margin + 5, yPos + 5.5);
    yPos += 14;

    const tableX = margin;
    const tableWidth = contentWidth;
    
    addonsData.forEach(addon => {
      const label = addon.quantity > 1 ? `${addon.name} (x${addon.quantity})` : addon.name;
      yPos = drawTableRow(pdf, tableX, yPos, label, fp(addon.price), tableWidth);
    });

    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tableX, yPos - 2, tableX + tableWidth, yPos - 2);
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Servicios', fp(totals?.addonsSubtotal || 0), tableWidth, { isBold: true });
    
    yPos += 5;
  }

  // ========== EDITORIAL SERVICES SECTION ==========
  const editorialData = quoteData.editorialData;
  if (editorialData && editorialData.subtotal > 0) {
    checkPageBreak(40);
    
    // Section header
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Servicios Editoriales', margin + 5, yPos + 5.5);
    yPos += 14;

    const tableX = margin;
    const tableWidth = contentWidth;
    
    if (editorialData.maquetacion && editorialData.maquetacionTotal > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Maquetación físico y digital', editorialData.maquetacionTotal, tableWidth);
    }
    if (editorialData.correccion && editorialData.correccionTotal > 0) {
      const label = editorialData.correccionCuartillas > 0 
        ? `Corrección de estilo (${editorialData.correccionCuartillas} cuartillas)` 
        : 'Corrección de estilo';
      yPos = drawRow(pdf, tableX, yPos, label, editorialData.correccionTotal, tableWidth);
    }
    if (editorialData.portadaFisica && editorialData.portadaFisicaTotal > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Diseño de portada libro físico', editorialData.portadaFisicaTotal, tableWidth);
    }
    if (editorialData.impresion && editorialData.impresionTotal > 0) {
      const label = editorialData.impresionCantidad > 0 
        ? `Impresión de libros (${editorialData.impresionCantidad} unidades)` 
        : 'Impresión de libros';
      yPos = drawRow(pdf, tableX, yPos, label, editorialData.impresionTotal, tableWidth);
    }
    if (editorialData.ghostWriting && editorialData.ghostWritingTotal > 0) {
      yPos = drawRow(pdf, tableX, yPos, 'Ghost Writing', editorialData.ghostWritingTotal, tableWidth);
    }
    
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tableX, yPos - 2, tableX + tableWidth, yPos - 2);
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Editorial', fp(editorialData.subtotal), tableWidth, { isBold: true });
    
    yPos += 5;
  }

  // ========== RENTA DE EQUIPO SECTION ==========
  const rentaData = quoteData.rentaEquipoData;
  const rentaTotal = quoteData.rentaEquipoTotal || 0;
  if (rentaData && rentaTotal > 0) {
    checkPageBreak(35);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Renta de Equipo', margin + 5, yPos + 5.5);
    yPos += 14;
    const tX = margin, tW = contentWidth;
    if (rentaData.microfonoQty > 0 && rentaData.microfonoDias > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Micrófono Shure (${rentaData.microfonoQty} × ${rentaData.microfonoDias} días)`, fp(497 * rentaData.microfonoQty * rentaData.microfonoDias), tW);
    if (rentaData.camaraDias > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Cámara Canon MR10 (${rentaData.camaraDias} días)`, fp(1850 * rentaData.camaraDias), tW);
    if (rentaData.lavalierQty > 0 && rentaData.lavalierDias > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Lavalier Sennheiser (${rentaData.lavalierQty} × ${rentaData.lavalierDias} días)`, fp(750 * rentaData.lavalierQty * rentaData.lavalierDias), tW);
    if (rentaData.teleprompterDias > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Teleprompter (${rentaData.teleprompterDias} días)`, fp(897 * rentaData.teleprompterDias), tW);
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tX, yPos - 2, tX + tW, yPos - 2);
    yPos = drawTableRow(pdf, tX, yPos, 'Subtotal Renta de Equipo', fp(rentaTotal), tW, { isBold: true });
    yPos += 5;
  }

  // ========== TRANSMISIÓN EN VIVO SECTION ==========
  const transData = quoteData.transmisionData;
  const transTotal = quoteData.transmisionTotal || 0;
  if (transData && transTotal > 0) {
    checkPageBreak(25);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Transmisión en Vivo', margin + 5, yPos + 5.5);
    yPos += 14;
    const tX = margin, tW = contentWidth;
    if (transData.basicQty > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Basic Uniplataforma (×${transData.basicQty})`, fp(697 * transData.basicQty), tW);
    if (transData.proDias > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Pro Multiplataforma (${transData.proDias} días)`, fp(3997 * transData.proDias), tW);
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tX, yPos - 2, tX + tW, yPos - 2);
    yPos = drawTableRow(pdf, tX, yPos, 'Subtotal Transmisión', fp(transTotal), tW, { isBold: true });
    yPos += 5;
  }

  // ========== SONIDO DIRECTO SECTION ==========
  const sonidoData = quoteData.sonidoDirectoData;
  const sonidoTotal = quoteData.sonidoDirectoTotal || 0;
  if (sonidoData && sonidoTotal > 0) {
    checkPageBreak(25);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Sonido Directo', margin + 5, yPos + 5.5);
    yPos += 14;
    const tX = margin, tW = contentWidth;
    if (sonidoData.basicProHoras > 0)
      yPos = drawTableRow(pdf, tX, yPos, `BásicoPro (${sonidoData.basicProHoras} hrs)`, fp(350 * sonidoData.basicProHoras), tW);
    if (sonidoData.proHoras > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Pro (${sonidoData.proHoras} hrs)`, fp(997 * sonidoData.proHoras), tW);
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tX, yPos - 2, tX + tW, yPos - 2);
    yPos = drawTableRow(pdf, tX, yPos, 'Subtotal Sonido Directo', fp(sonidoTotal), tW, { isBold: true });
    yPos += 5;
  }

  // ========== FORO SECTION ==========
  const foroData = quoteData.foroData;
  const foroTotalPdf = quoteData.foroTotal || 0;
  if (foroData && foroTotalPdf > 0) {
    checkPageBreak(30);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Foro', margin + 5, yPos + 5.5);
    yPos += 14;
    const tX = margin, tW = contentWidth;
    if (foroData.santaFeHoras > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Foro Santa Fe (${foroData.santaFeHoras} hrs)`, fp(1000 * foroData.santaFeHoras), tW);
    if (foroData.masivoDias > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Foro Masivo (${foroData.masivoDias} días)`, fp(30000 * foroData.masivoDias), tW);
    if (foroData.storageDias > 0)
      yPos = drawTableRow(pdf, tX, yPos, `Storage (${foroData.storageDias} días)`, fp(97 * foroData.storageDias), tW);
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tX, yPos - 2, tX + tW, yPos - 2);
    yPos = drawTableRow(pdf, tX, yPos, 'Subtotal Foro', fp(foroTotalPdf), tW, { isBold: true });
    yPos += 5;
  }

  // ========== INTERPRETACIÓN SIMULTÁNEA SECTION ==========
  const interpData = quoteData.interpretacionData;
  const interpTotal = quoteData.interpretacionTotal || 0;
  if (interpData && interpTotal > 0) {
    checkPageBreak(40);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Interpretación Simultánea', margin + 5, yPos + 5.5);
    yPos += 14;
    const tX = margin, tW = contentWidth;
    const effHrs = interpData.horas > 0 && interpData.horas < 4 ? 4 : (interpData.horas || 0);
    const dias = interpData.dias || 1;
    if (interpData.horas > 0) {
      yPos = drawTableRow(pdf, tX, yPos, `Intérprete Bilingüe (${effHrs} hrs × ${dias} día${dias > 1 ? 's' : ''})`, fp(1500 * effHrs * dias), tW);
    }
    if (interpData.cabina) {
      const cabDias = interpData.cabinaDias || dias;
      yPos = drawTableRow(pdf, tX, yPos, `Cabina de audio (${cabDias} día${cabDias > 1 ? 's' : ''})`, fp(7500 * cabDias), tW);
    }
    if ((interpData.equipoTxQty || 0) > 0 && (interpData.equipoTxDias || 0) > 0) {
      yPos = drawTableRow(pdf, tX, yPos, `Equipo TX/RX portátil (${interpData.equipoTxQty} × ${interpData.equipoTxDias} días)`, fp(450 * interpData.equipoTxQty * interpData.equipoTxDias), tW);
    }
    if ((interpData.tecnicoDias || 0) > 0) {
      yPos = drawTableRow(pdf, tX, yPos, `Técnico de audio (${interpData.tecnicoDias} día${interpData.tecnicoDias > 1 ? 's' : ''})`, fp(1500 * interpData.tecnicoDias), tW);
    }
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tX, yPos - 2, tX + tW, yPos - 2);
    yPos = drawTableRow(pdf, tX, yPos, 'Subtotal Interpretación', fp(interpTotal), tW, { isBold: true });
    // Note about lost equipment
    pdf.setFontSize(6);
    pdf.setTextColor(150, 150, 150);
    pdf.setFont('helvetica', 'italic');
    pdf.text('Nota: En caso de pérdida o daño al receptor: $220 USD por receptor, $50 USD por audífono.', tX, yPos + 2);
    yPos += 8;
  }

  // ========== SONORIZACIÓN SECTION ==========
  const sonoData = quoteData.sonorizacionData;
  const sonoTotal = quoteData.sonorizacionTotal || 0;
  if (sonoData && sonoTotal > 0) {
    checkPageBreak(25);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Sonorización para Eventos', margin + 5, yPos + 5.5);
    yPos += 14;
    const tX = margin, tW = contentWidth;
    const pkgName = sonoData.package === 'sono_essential' ? 'Essential' : sonoData.package === 'sono_plus' ? 'Plus' : 'Premium';
    yPos = drawTableRow(pdf, tX, yPos, `Paquete ${pkgName} (${sonoData.dias || 1} día${(sonoData.dias || 1) > 1 ? 's' : ''})`, fp(sonoTotal), tW);
    // Section separator removed
    // pdf.setDrawColor(...COLORS.muted);
    // pdf.line(tX, yPos - 2, tX + tW, yPos - 2);
    yPos = drawTableRow(pdf, tX, yPos, 'Subtotal Sonorización', fp(sonoTotal), tW, { isBold: true });
    yPos += 5;
  }

  // ========== GRAND TOTAL SECTION ==========
  if (totals) {
    checkPageBreak(50);
    
    yPos += 5;
    

  // ========== VOICE SERVICES SECTION ==========
  const voiceServicesQty = quoteData.voiceServicesQuantities || {};
  const voiceServicesTotal = quoteData.voiceServicesTotal || 0;
  if (voiceServicesTotal > 0) {
    checkPageBreak(30);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Servicios de Voz', margin + 5, yPos + 5.5);
    yPos += 14;

    const tableX = margin;
    const tableWidth = contentWidth;
    
    // Import VOICE_CATEGORIES data inline
    const VOICE_CATS = [
      { items: [
        { id: 'radio_nacional', name: 'Radio Tradicional — Nacional', price: 4500 },
        { id: 'radio_regional', name: 'Radio Tradicional — Regional', price: 3600 },
        { id: 'radio_local', name: 'Radio Tradicional — Local', price: 2250 },
        { id: 'tv_nacional', name: 'Televisión Abierta — Nacional', price: 8000 },
        { id: 'tv_regional', name: 'Televisión Abierta — Regional', price: 5000 },
        { id: 'tv_local', name: 'Televisión Abierta — Local', price: 4000 },
        { id: 'tv_latam', name: 'TV Pan Cable / LATAM', price: 24000 },
        { id: 'cine_nacional', name: 'Cine — Nacional', price: 6000 },
        { id: 'radio_internet', name: 'Radio por Internet (No Streaming)', price: 4500 },
        { id: 'radio_streaming', name: 'Radio por Internet (Streaming Live)', price: 5000 },
        { id: 'web_sitio', name: 'Internet / Web — Sitio Cliente', price: 3000 },
        { id: 'web_preroll', name: 'Internet / Web — Pre Roll', price: 5000 },
        { id: 'web_red_social', name: 'Internet / Web — Red Social', price: 3000 },
        { id: 'video_explicativo', name: 'Video Promocional Explicativo', price: 8000 },
        { id: 'yt_inst_1min', name: 'YouTube Institucional (hasta 1 min)', price: 5000 },
        { id: 'yt_talento_1min', name: 'YouTube Talento/Personaje (hasta 1 min)', price: 4500 },
        { id: 'yt_narracion_1min', name: 'YouTube Narración (hasta 1 min)', price: 8000 },
        { id: 'yt_inst_5min', name: 'YouTube Institucional (3-5 min)', price: 8000 },
        { id: 'yt_talento_5min', name: 'YouTube Talento/Personaje (3-5 min)', price: 8000 },
        { id: 'yt_narracion_5min', name: 'YouTube Narración (3-5 min)', price: 12000 },
        { id: 'paq_social', name: 'Paquete Full Social Media', price: 8000 },
        { id: 'paq_web', name: 'Paquete Full Web', price: 15000 },
        { id: 'iguala_radio_local', name: 'Iguala Radio — Local', price: 7000 },
        { id: 'iguala_radio_regional', name: 'Iguala Radio — Regional', price: 14000 },
        { id: 'iguala_radio_nacional', name: 'Iguala Radio — Nacional', price: 28000 },
        { id: 'iguala_tv_nacional', name: 'Iguala TV — Nacional', price: 20000 },
        { id: 'iguala_tv_intl', name: 'Iguala TV — Internacional', price: 30000 },
        { id: 'iguala_tv_paga', name: 'Iguala TV Paga — Nacional', price: 12000 },
        { id: 'iguala_tv_paga_intl', name: 'Iguala TV Paga — Internacional', price: 35000 },
        { id: 'iguala_tv_cultural', name: 'Iguala TV Paga — Canal Cultural', price: 8000 },
        { id: 'maquetas', name: 'Maquetas / Animatics', price: 3000 },
        { id: 'circuito_cerrado', name: 'Circuito Cerrado', price: 3000 },
        { id: 'video_interno_min', name: 'Video Interno — Min. inicial', price: 3000 },
        { id: 'video_interno_extra', name: 'Video Interno — Min. extra', price: 1500 },
        { id: 'voces_productos', name: 'Voces para Productos', price: 2000 },
        { id: 'videojuegos', name: 'Videojuegos / Audiolibros (hora)', price: 1750 },
        { id: 'mobile_5min', name: 'Mobile Game Apps (hasta 5 min)', price: 7500 },
        { id: 'mobile_5min_plus', name: 'Mobile Game Apps (más de 5 min)', price: 12000 },
        { id: 'pantallas_pos', name: 'Pantallas POS', price: 3500 },
        { id: 'perifoneo', name: 'Perifoneo', price: 4000 },
        { id: 'aerolineas', name: 'Aerolíneas — Mensajes a bordo', price: 1750 },
        { id: 'aviso_corto', name: 'Aviso Privacidad — Corto', price: 2000 },
        { id: 'aviso_extenso', name: 'Aviso Privacidad — Extenso', price: 3500 },
        { id: 'trailer_general', name: 'Trailers Películas — General', price: 7000 },
        { id: 'trailer_internet', name: 'Trailers Películas — Internet', price: 5000 },
        { id: 'trailer_ambas', name: 'Trailers Películas — Ambas', price: 12000 },
        { id: 'podcast_intro', name: 'Podcasts — Intro/Outro', price: 5000 },
        { id: 'infomercial_principal', name: 'Infomercial — Principal', price: 8000 },
        { id: 'infomercial_secundario', name: 'Infomercial — Secundario', price: 5000 },
        { id: 'infomercial_incidental', name: 'Infomercial — Incidental', price: 2500 },
        { id: 'ivr_bienvenida', name: 'IVR — Bienvenida', price: 3500 },
        { id: 'ivr_menu', name: 'IVR — Menú Individual', price: 800 },
        { id: 'ivr_espera', name: 'IVR — Mensaje Espera', price: 2600 },
        { id: 'elearn_session', name: 'E-Learning — Session Free', price: 5500 },
        { id: 'elearn_5min', name: 'E-Learning — 5 min', price: 6000 },
        { id: 'elearn_15min', name: 'E-Learning — 15 min', price: 16000 },
        { id: 'elearn_20min', name: 'E-Learning — 20 min', price: 20000 },
        { id: 'elearn_45min', name: 'E-Learning — 45 min', price: 24000 },
        { id: 'elearn_60min', name: 'E-Learning — 60 min', price: 30000 },
      ]}
    ];

    VOICE_CATS[0].items.forEach(item => {
      const qty = voiceServicesQty[item.id] || 0;
      if (qty > 0) {
        const lineTotal = item.price * qty;
        const label = qty > 1 ? `${item.name} (×${qty})` : item.name;
        yPos = drawRow(pdf, tableX, yPos, label, lineTotal, tableWidth);
        checkPageBreak(8);
      }
    });

    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Servicios de Voz', fp(voiceServicesTotal), tableWidth, { isBold: true });
    yPos += 5;
  }



  // ========== STUDIO FEES SECTION ==========
  const studioData = quoteData.studioHours || {};
  const audioPostH = quoteData.audioPostHours || 0;
  const studioFeesTotal = quoteData.studioFeesTotal || 0;
  if (studioFeesTotal > 0) {
    checkPageBreak(30);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Studio Fees', margin + 5, yPos + 5.5);
    yPos += 14;
    const tableX = margin;
    const tableWidth = contentWidth;
    if ((studioData.premium || 0) > 0) {
      const price = 3500;
      yPos = drawRow(pdf, tableX, yPos, `Studio Premium (${studioData.premium}h)`, studioData.premium * price, tableWidth);
    }
    if ((studioData.essential || 0) > 0) {
      const price = 2000;
      yPos = drawRow(pdf, tableX, yPos, `Studio Essential (${studioData.essential}h)`, studioData.essential * price, tableWidth);
    }
    if (audioPostH > 0) {
      const price = 1500;
      yPos = drawRow(pdf, tableX, yPos, `AudioPost (${audioPostH}h)`, audioPostH * price, tableWidth);
    }
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Studio Fees', fp(studioFeesTotal), tableWidth, { isBold: true });
    yPos += 5;
  }

  // ========== HAND-FREE SECTION ==========
  const handFreeRows = quoteData.handFreeRows || [];
  const handFreeTotal = quoteData.handFreeTotal || 0;
  if (handFreeTotal > 0) {
    checkPageBreak(30);
    drawGradientRect(margin, yPos, contentWidth, 8);
    pdf.setFontSize(FONT_SIZES.sectionHeader);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Hand-Free', margin + 5, yPos + 5.5);
    yPos += 14;
    const tableX = margin;
    const tableWidth = contentWidth;
    handFreeRows.filter(r => r.unitPrice > 0 && r.quantity > 0).forEach(row => {
      const lineTotal = row.unitPrice * row.quantity;
      const label = `${row.description || 'Servicio'} (${row.quantity} ${row.unit})`;
      yPos = drawRow(pdf, tableX, yPos, label, lineTotal, tableWidth);
      checkPageBreak(8);
    });
    yPos = drawTableRow(pdf, tableX, yPos, 'Subtotal Hand-Free', fp(handFreeTotal), tableWidth, { isBold: true });
    yPos += 5;
  }


    // ViubuxConnect in PDF
    if (totals.viubuxConnectActive && totals.viubuxConnectInscription > 0) {
      // Section header
      pdf.setFillColor(0, 194, 255); // #00c2ff
      pdf.roundedRect(margin, yPos, contentWidth, 8, 2, 2, 'F');
      pdf.setFontSize(FONT_SIZES.sectionHeader);
      pdf.setTextColor(...COLORS.white);
      pdf.setFont('helvetica', 'bold');
      pdf.text('ViubuxConnect', margin + 5, yPos + 5.5);
      yPos += 14;

      const vcX = margin;
      const vcW = contentWidth;
      
      // Inscription row
      pdf.setTextColor(0, 194, 255);
      yPos = drawTableRow(pdf, vcX, yPos, 'Inscripción ViubuxConnect', `+${fp(totals.viubuxConnectInscription)}`, vcW);
      
      // Catálogo 12 meses row
      if (totals.vcCatalogoCost > 0) {
        yPos = drawTableRow(pdf, vcX, yPos, '12 meses Catálogo ViubuxConnect', `+${fp(totals.vcCatalogoCost)}`, vcW);
      }
      
      // Discount row - only show lump sum when NOT showing per-item discounts
      if (!showVCDiscount) {
        pdf.setTextColor(...COLORS.green);
        yPos = drawTableRow(pdf, vcX, yPos, 'Descuento ViubuxConnect (15%)', `-${fp(totals.viubuxConnectDiscountAmount || 0)}`, vcW);
      }
      
      pdf.setTextColor(...COLORS.text);
      yPos += 5;
    }
    
    // Total section with gradient-like background
    pdf.setFillColor(248, 248, 252);
    pdf.rect(margin, yPos - 3, contentWidth, 45, 'F');
    
    const summaryX = margin + 5;
    const summaryWidth = contentWidth - 10;
    
    // Custom discount row
    if (totals.customDiscount > 0) {
      pdf.setTextColor(...COLORS.green);
      yPos = drawTableRow(pdf, summaryX, yPos, `Descuento especial (${totals.customDiscountType === 'fixed' ? fp(totals.customDiscount) : totals.customDiscount + '%'})`, `-${fp(totals.discountAmount || 0)}`, summaryWidth);
      pdf.setTextColor(...COLORS.text);
      yPos += 2;
    }
    
    // Subtotal separator
    pdf.setDrawColor(170, 42, 255);
    pdf.setLineWidth(0.5);
    pdf.line(summaryX, yPos - 2, summaryX + summaryWidth, yPos - 2);
    yPos = drawTableRow(pdf, summaryX, yPos, 'Subtotal', fp(totals.subtotal || 0), summaryWidth);
    
    // IVA
    if (totals.iva > 0) {
      yPos = drawTableRow(pdf, summaryX, yPos, 'IVA (16%)', `+${fp(totals.iva)}`, summaryWidth);
    }
    
    yPos += 3;

    // Grand Total highlight box with gradient
    const totalBoxX = summaryX - 3;
    const totalBoxW = summaryWidth + 6;
    drawGradientRect(totalBoxX, yPos - 2, totalBoxW, 12);
    pdf.setFontSize(11);
    pdf.setTextColor(...COLORS.white);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TOTAL A INVERTIR', summaryX + 2, yPos + 6);
    pdf.setFontSize(14);
    pdf.text(fp(totals.grandTotal || 0), summaryX + summaryWidth - 2, yPos + 6, { align: 'right' });
    
    yPos += 16;
    
    // Co-Producción section in PDF
    if (totals.showCoproduccion && totals.coproduccionAmount > 0) {
      checkPageBreak(25);
      
      // Co-investor contribution line
      pdf.setFontSize(FONT_SIZES.body);
      pdf.setTextColor(212, 160, 23); // amber
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Aportación Co-Inversionista (${totals.coproduccionPercent}%)`, summaryX + 2, yPos);
      pdf.text(`-${fp(totals.coproduccionAmount)}`, summaryX + summaryWidth - 2, yPos, { align: 'right' });
      yPos += 8;
      
      // Client amount box with gradient
      drawGradientRect(summaryX - 3, yPos - 2, summaryWidth + 6, 12);
      pdf.setFontSize(11);
      pdf.setTextColor(...COLORS.white);
      pdf.text('TU INVERSIÓN', summaryX + 2, yPos + 6);
      pdf.setFontSize(14);
      pdf.text(fp(totals.clientPayAmount || 0), summaryX + summaryWidth - 2, yPos + 6, { align: 'right' });
      yPos += 16;
    }

    yPos += 4;
  }

  // ========== PAYMENT PLAN OPTIONS ==========
  {
    const payable = (totals.showCoproduccion && totals.coproduccionAmount > 0) ? (totals.clientPayAmount || 0) : (totals.grandTotal || 0);
    if (payable > 0) {
      checkPageBreak(28);
      pdf.setFontSize(FONT_SIZES.body);
      pdf.setTextColor(...COLORS.text);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Opciones de Pago', margin, yPos + 4);
      yPos += 8;

      const boxW = (contentWidth - 4) / 2;
      const withSurcharge = payable * 1.16;
      const perInstallment = withSurcharge / 3;

      pdf.setDrawColor(220, 220, 220);
      pdf.setFillColor(248, 248, 250);
      pdf.roundedRect(margin, yPos, boxW, 16, 2, 2, 'FD');
      pdf.setFontSize(FONT_SIZES.small);
      pdf.setTextColor(...COLORS.muted);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Contado (1 exhibición)', margin + 3, yPos + 5);
      pdf.setFontSize(FONT_SIZES.body);
      pdf.setTextColor(...COLORS.text);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${fp(payable)} ${pdfCurrency}`, margin + 3, yPos + 12);

      pdf.setDrawColor(170, 42, 255);
      pdf.setFillColor(248, 240, 255);
      pdf.roundedRect(margin + boxW + 4, yPos, boxW, 16, 2, 2, 'FD');
      pdf.setFontSize(FONT_SIZES.small);
      pdf.setTextColor(170, 42, 255);
      pdf.setFont('helvetica', 'normal');
      pdf.text('3 pagos (+16%)', margin + boxW + 7, yPos + 5);
      pdf.setFontSize(FONT_SIZES.body);
      pdf.setTextColor(170, 42, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`3 × ${fp(perInstallment)}`, margin + boxW + 7, yPos + 11);
      pdf.setFontSize(FONT_SIZES.small);
      pdf.setTextColor(...COLORS.muted);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Total: ${fp(withSurcharge)} ${pdfCurrency}`, margin + boxW + 7, yPos + 15);

      yPos += 22;
    }
  }

  // ========== EXCHANGE RATE NOTE (USD only) ==========
  if (pdfCurrency === 'USD') {
    checkPageBreak(15);
    pdf.setFillColor(240, 249, 255);
    pdf.roundedRect(margin, yPos - 2, contentWidth, 10, 2, 2, 'F');
    pdf.setFontSize(FONT_SIZES.body);
    pdf.setTextColor(...COLORS.secondary);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Tipo de Cambio aplicado: 1 USD = $${Number(pdfExRate).toFixed(2)} MXN`, margin + 5, yPos + 4);
    yPos += 14;
  }

  // ========== VALIDITY NOTE ==========
  checkPageBreak(20);
  
  pdf.setFontSize(FONT_SIZES.small);
  pdf.setTextColor(...COLORS.muted);
  pdf.setFont('helvetica', 'normal');
  
  const validityDate = new Date();
  validityDate.setDate(validityDate.getDate() + 30);
  const validUntil = validityDate.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  
  pdf.text(`Cotización válida hasta: ${validUntil}`, margin, yPos);
  yPos += 4;
  if (quoteData.quoteNumber) {
    pdf.setTextColor(170, 42, 255);
    pdf.setFont('helvetica', 'bold');
    const recoveryUrl = `https://cotizaciones.viubux.com?code=${quoteData.quoteNumber}`;
    pdf.textWithLink(`Recupera tu cotización: ${recoveryUrl}`, margin, yPos, { url: recoveryUrl });
    yPos += 4;
  }

  // ========== ADD FOOTERS TO ALL PAGES ==========
  const totalPages = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    addFooter(pdf, pdfWidth, pdfHeight, margin, i, totalPages);
  }

  // Save the PDF
  if (quoteData._returnBlob) {
    return pdf.output('blob');
  }
  pdf.save(`${filename}.pdf`);
  return true;
}

/**
 * Legacy function for backwards compatibility
 */
export async function exportToPDF(element, filename = 'cotizacion-viubux') {
  return exportQuoteToPDF(element, {});
}
